import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useInventoryStore } from '../store/inventoryStore';
import CustomDropdown from './CustomDropdown';
import MultiSelectDropdown from './MultiSelectDropdown';
import { useGlobalLoading } from '../hooks/useGlobalLoading';
import { useDebounce } from 'use-debounce';

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 16px;
    row-gap: 16px;
  }
`;

const FilterContainer = styled.div`
  width: 100%;

  @media (min-width: 768px) {
    width: 160px;
    flex-shrink: 0;
  }
`;

const PriceContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (min-width: 768px) {
    width: 460px;
    flex-shrink: 0;
  }
`;

const PriceInputsWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PriceInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.04);
  color: var(--color-text);
  font-family: var(--font-family-main);
  font-size: 14px;
  line-height: 1.5;
  min-height: 40px;
  transition: all 0.2s ease;
  
  /* Скрываем стандартные стрелки */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &[type=number] {
    -moz-appearance: textfield;
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const PriceSeparator = styled.span`
  color: rgba(255, 255, 255, 0.4);
  font-family: var(--font-family-main);
`;

const Label = styled.label`
  font-family: var(--font-family-main);
  font-weight: 400;
  font-size: 12px;
  color: var(--color-text-secondary);
  margin-bottom: 4px;
  display: block;
`;

const CompactDropdownWrapper = styled.div`
  /* Переопределяем стили дропдауна для более компактного вида */
  button {
    padding: 8px 12px !important;
    font-size: 14px !important;
    min-height: 40px !important;
  }
  
  label {
    font-size: 12px !important;
  }
`;

const ClearButton = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid #FF6B6B;
  background-color: transparent;
  color: #FF6B6B;
  font-family: var(--font-family-main);
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  min-height: 40px;
  align-self: flex-end;
  
  &:hover {
    background-color: #FF6B6B;
    color: #FFFFFF;
  }
`;

const InventoryFilters: React.FC = () => {
  const {
    selectedBrandId,
    selectedModelId,
    selectedArticleId,
    selectedSize,
    priceFrom,
    priceTo,
    isSale,
    availableBrandsWithId,
    availableModels,
    availableArticles,
    availableSizes,
    isLoadingBrands,
    isLoadingModels,
    isLoadingArticles,
    setSelectedBrandId,
    setSelectedModelId,
    setSelectedArticleId,
    setSelectedSize,
    setPriceFrom,
    setPriceTo,
    setIsSale,
    fetchAllBrands,
    fetchAllModels,
    fetchAllArticles
  } = useInventoryStore();

  const { addLoadingStep, removeLoadingStep } = useGlobalLoading();

  const [localPriceFrom, setLocalPriceFrom] = useState(priceFrom?.toString() || '');
  const [localPriceTo, setLocalPriceTo] = useState(priceTo?.toString() || '');
  const [isFirstRender, setIsFirstRender] = useState(true);

  // Debounced значения для цены (задержка 800мс)
  const [debouncedPriceFrom] = useDebounce(priceFrom, 800);
  const [debouncedPriceTo] = useDebounce(priceTo, 800);

  useEffect(() => {
    setLocalPriceFrom(priceFrom?.toString() || '');
  }, [priceFrom]);

  useEffect(() => {
    setLocalPriceTo(priceTo?.toString() || '');
  }, [priceTo]);

  // Загружаем все данные для фильтров при монтировании компонента
  useEffect(() => {
    const loadFilterData = async () => {
      await Promise.all([
        fetchAllBrands(),
        fetchAllModels(),
        fetchAllArticles()
      ]);
    };

    loadFilterData();
  }, [fetchAllBrands, fetchAllModels, fetchAllArticles]);

  const handlePriceFromChange = (value: string) => {
    // Запрещаем отрицательные значения
    if (value && parseInt(value) < 0) return;
    
    setLocalPriceFrom(value);
    const numValue = value ? parseInt(value) : null;
    setPriceFrom(numValue);
  };

  const handlePriceToChange = (value: string) => {
    // Запрещаем отрицательные значения
    if (value && parseInt(value) < 0) return;
    
    setLocalPriceTo(value);
    const numValue = value ? parseInt(value) : null;
    setPriceTo(numValue);
  };

  // Подготовка опций для дропдаунов
  const brandOptions = [
    { value: "", label: isLoadingBrands ? "Загрузка..." : "Все бренды" },
    ...(availableBrandsWithId?.map((brand) => ({
      value: brand.id.toString(),
      label: brand.name,
    })) || []),
  ];

  const modelOptions = [
    { value: "", label: isLoadingModels ? "Загрузка..." : "Все модели" },
    ...(availableModels?.map((model) => ({
      value: model,
      label: model,
    })) || []),
  ];

  const articleOptions = [
    { value: "", label: isLoadingArticles ? "Загрузка..." : "Все артикулы" },
    ...(availableArticles?.map((article) => ({
      value: article,
      label: article,
    })) || []),
  ];

  const sizeOptions = [
    { value: "", label: "Все размеры" },
    ...(availableSizes?.map((size) => ({
      value: size,
      label: size,
    })) || []),
  ];

  const handleBrandChange = (value: string) => {
    setSelectedBrandId(value ? parseInt(value) : null);
  };

  const handleModelChange = (value: string) => {
    setSelectedModelId(value || null);
  };

  const handleArticleChange = (value: string) => {
    setSelectedArticleId(value || null);
  };

  const handleSizeChange = (value: string) => {
    setSelectedSize(value || null);
  };

  // Автоматическое применение фильтров при изменении
  useEffect(() => {
    // Пропускаем первый рендер
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }

    const applyFiltersAsync = async () => {
      const loadingId = 'apply-filters';
      try {
        addLoadingStep(loadingId, 'Применение фильтров...');
        await useInventoryStore.getState().applyFilters();
      } finally {
        removeLoadingStep(loadingId);
      }
    };

    applyFiltersAsync();
  }, [selectedBrandId, selectedModelId, selectedArticleId, selectedSize, debouncedPriceFrom, debouncedPriceTo, isSale]);

  const handleClearFilters = () => {
    const { clearFilters } = useInventoryStore.getState();
    clearFilters();
    setLocalPriceFrom('');
    setLocalPriceTo('');
  };

  return (
    <FiltersContainer>
      <FilterContainer>
        <CompactDropdownWrapper>
          <CustomDropdown
            options={brandOptions}
            value={selectedBrandId?.toString() || ""}
            placeholder="Выберите бренд"
            onChange={handleBrandChange}
            label="Бренд"
            disabled={isLoadingBrands}
          />
        </CompactDropdownWrapper>
      </FilterContainer>

      <FilterContainer>
        <CompactDropdownWrapper>
          <CustomDropdown
            options={modelOptions}
            value={selectedModelId || ""}
            placeholder="Выберите модель"
            onChange={handleModelChange}
            label="Модель"
            disabled={isLoadingModels}
          />
        </CompactDropdownWrapper>
      </FilterContainer>

      <FilterContainer>
        <CompactDropdownWrapper>
          <CustomDropdown
            options={articleOptions}
            value={selectedArticleId || ""}
            placeholder="Выберите артикул"
            onChange={handleArticleChange}
            label="Артикул"
            disabled={isLoadingArticles}
          />
        </CompactDropdownWrapper>
      </FilterContainer>

      <FilterContainer>
        <CompactDropdownWrapper>
          <CustomDropdown
            options={sizeOptions}
            value={selectedSize || ""}
            placeholder="Выберите размер"
            onChange={handleSizeChange}
            label="Размер"
          />
        </CompactDropdownWrapper>
      </FilterContainer>

      <PriceContainer>
        <Label>Цена</Label>
        <PriceInputsWrapper>
          <PriceInput
            type="number"
            placeholder="От"
            value={localPriceFrom}
            onChange={(e) => handlePriceFromChange(e.target.value)}
            min="0"
          />
          <PriceSeparator>—</PriceSeparator>
          <PriceInput
            type="number"
            placeholder="До"
            value={localPriceTo}
            onChange={(e) => handlePriceToChange(e.target.value)}
            min="0"
          />
        </PriceInputsWrapper>
      </PriceContainer>

      <ClearButton onClick={handleClearFilters}>
        Сбросить
      </ClearButton>
    </FiltersContainer>
  );
};

export default InventoryFilters;
